import React, { useState, useRef } from 'react';
import useWindowSize from '~/hooks/useWindowSize';
import {
  Box,
  Paper,
  Popper,
  Grow,
  ClickAwayListener,
  Modal,
  Typography,
  Button,
  Tooltip,
} from '@mui/material';
import { CloseRounded, UnfoldMore } from '@mui/icons-material';
import useAccountsQuery from '~/hooks/queries/useAccountsQuery';
import useSessionPlQuery from '~/hooks/queries/useSessionPlQuery';
import { useParams } from 'react-router';
import ClientsSelector from '~/components/Clients/ClientsSelector/ClientsSelector';
import SectionTitle from '~/components/Typography/SectionTitle';
import useLoginSeenCookie from '~/hooks/useLoginSeenCookie';
import defaultLogo from '~/assets/img/placeholder.jpg';
import useMenu from '~/context/useMiniMenu';
import { Accounts } from '../ClientsSelector/types';

type AccountsData =
  | {
      accounts: Accounts;
      emptyAccounts: boolean;
    }
  | null
  | undefined;

export const generateFullLogoPath = (relativelogoPath: string | null | undefined) => {
  if (!relativelogoPath) {
    return defaultLogo;
  }
  if (process.env.REACT_APP_ENVIRONMENT !== 'prod') {
    return `/preview${relativelogoPath}`;
  }
  return relativelogoPath;
};

interface NavbarClientSelectorProps {
  sidebarOpen: boolean;
  onClientChange?: () => void;
}

function NavbarClientSelector({ sidebarOpen, onClientChange }: NavbarClientSelectorProps) {
  // * State
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { accountType, accountId } = useParams();
  const buttonRef = useRef<HTMLButtonElement>(null);
  // * Hooks
  const { data: sessionData } = useSessionPlQuery();
  const { data: accountsData } = useAccountsQuery();
  const { isMobile } = useWindowSize();
  const { isOpen } = useMenu();
  const { setLoginSeenCookie } = useLoginSeenCookie();

  // * Functions
  const getBaseLogoPath = (accountData: AccountsData) => {
    if (accountId) {
      if (accountType === 'partner') {
        return accountData?.accounts.partners.find((partner) => {
          return partner.id.toString() === accountId;
        })?.logo;
      }
      return accountData?.accounts.clients
        .find((client) => {
          return client.id.toString() === accountId;
        })
        ?.logo?.replace(/https:\/\/(dev|www)\.dianomi\.com/, '');
    }
    return undefined;
  };

  // * Handlers
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setOpen((prevOpen) => !prevOpen);
  };
  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (buttonRef.current && buttonRef.current.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  // * Variables
  const baseLogoPath = getBaseLogoPath(accountsData);
  const isPlaceholderLogo = !baseLogoPath || baseLogoPath.toLowerCase().includes('placeholder');

  const displayLogo = generateFullLogoPath(baseLogoPath);
  const altLogoText =
    sessionData?.name?.split(' ')[0]?.replace(/[()]/g, '')?.slice(0, 3)?.toUpperCase() || '';
  const uniqueClient =
    accountsData?.accounts.clientsCount === 1 &&
    accountsData?.accounts.clients.length === 1 &&
    accountsData?.accounts.partnersCount === 0 &&
    accountsData?.accounts.flatAccounts.length === 1;

  const uniquePartner =
    accountsData?.accounts.clientsCount === 0 &&
    accountsData?.accounts.partners.length === 1 &&
    accountsData?.accounts.partnersCount === 1 &&
    accountsData?.accounts.flatAccounts.length === 1;

  const uniqueAccount = uniqueClient || uniquePartner;

  let allowCreateClient;
  if (!sessionData?.isPartner) {
    allowCreateClient = sessionData?.client_permissions.allow_create_client ?? false;
  }

  const emptyAccounts =
    accountsData?.accounts.clientsCount === 0 &&
    accountsData?.accounts.partnersCount === 0 &&
    accountsData?.accounts.flatAccounts.length === 0;

  let label = '';

  if (emptyAccounts) {
    label = 'No accounts available';
  } else if (sessionData?.isPartner) {
    if (sessionData?.client.has_active_child > 0) {
      label = 'Publisher Parent Account';
    } else {
      label = 'Publisher';
    }
  } else if (sessionData && sessionData?.client.has_active_child > 0) {
    label = 'Advertiser Parent Account';
  } else {
    label = 'Advertiser';
  }
  let isSingleTabMenu: boolean | undefined;
  if (accountsData) {
    const clients = accountsData.accounts.clients.length;
    const partners = accountsData.accounts.partners.length;
    isSingleTabMenu = (clients === 0 || partners === 0) && clients + partners > 0;
  }

  let buttonWidth = 'unset';
  if (isMobile) {
    buttonWidth = '100vw';
  } else if (sidebarOpen) {
    buttonWidth = '200px';
  }

  const buttonJustifyContent = isMobile || !sidebarOpen ? 'center' : 'space-between';

  const accountIsParent = sessionData?.isParent;

  return (
    <div
      style={{
        width: '100%',
        position: 'relative',
      }}
    >
      {isMobile && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: '16px',
            right: '16px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        />
      )}
      {(uniqueAccount || emptyAccounts) && !allowCreateClient ? (
        <>
          <Button
            href="#"
            sx={(theme) => ({
              display: 'flex',
              border: '2px solid',
              borderColor: 'transparent',
              flexDirection: 'row',
              justifyContent: 'start',
              alignItems: 'center',
              overflow: 'hidden',
              lineHeight: '35px',
              fontSize: isMobile ? '14px' : '12px',
              fontWeight: '600',
              textTransform: 'none',
              color: 'secondary.main',
              whiteSpace: 'break-spaces',
              padding: sidebarOpen ? '5px 10px' : '5px',
              borderRadius: '4px',
              width: buttonWidth,
              backgroundColor: theme.palette.dianomiGrey.main,
              gap: sidebarOpen ? '8px' : '0px',
              margin: isMobile ? '10px 0px' : '0px',
              transition: 'all 250ms ease-out',
              ':hover': {
                color: 'secondary.main',
                cursor: 'default',
              },
            })}
            onClick={(e) => {
              e.preventDefault();
              return false;
            }}
          >
            {isPlaceholderLogo ? (
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: '600',
                  width: sidebarOpen ? '32px' : '38px',
                  height: sidebarOpen ? '32px' : '38px',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                  backgroundColor: 'secondary.main',
                  flexShrink: 0,
                  transition: 'all 250ms ease-out',
                }}
              >
                {altLogoText || '...'}
              </Typography>
            ) : (
              <Box
                component="img"
                src={displayLogo}
                alt={sessionData?.name}
                sx={{
                  width: sidebarOpen && !isMobile ? '32px' : '38px',
                  height: sidebarOpen && !isMobile ? '32px' : '38px',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  objectFit: 'contain',
                  flexShrink: 0,
                  transition: 'all 250ms ease-out',
                }}
              />
            )}
            {uniqueAccount && sessionData && (
              <Box
                sx={{
                  display: sidebarOpen ? 'flex' : 'none',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'start',
                  opacity: sidebarOpen ? 1 : 0,
                  overflow: 'hidden',
                  transition: 'opacity 150ms ease-out 150ms',
                  ml: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: isMobile ? '12px' : '11px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: isMobile ? '300px' : '130px',
                    color: 'primary.main',
                    padding: '0px',
                  }}
                >
                  {sessionData.name}
                </Typography>
                <Typography
                  sx={(theme) => ({
                    color: theme.palette.dianomiGreen.main,
                    fontSize: '11px',
                    fontWeight: '600',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                  })}
                >
                  {label}
                </Typography>
              </Box>
            )}
          </Button>
        </>
      ) : (
        <>
          <Tooltip
            title={sessionData?.name}
            slotProps={{
              popper: {
                sx: { zIndex: 4000 },
              },
            }}
          >
            <Button
              sx={(theme) => ({
                display: 'flex',
                border: '2px solid',
                borderColor: 'transparent',
                flexDirection: 'row',
                justifyContent: buttonJustifyContent,
                alignItems: 'center',
                overflow: 'hidden',
                lineHeight: '35px',
                fontSize: isMobile ? '14px' : '12px',
                fontWeight: '600',
                textTransform: 'none',
                color: 'secondary.main',
                whiteSpace: 'break-spaces',
                padding: sidebarOpen ? '5px 10px' : '5px',
                borderRadius: '4px',
                width: buttonWidth,
                backgroundColor: theme.palette.dianomiGrey.main,
                gap: !sidebarOpen || accountIsParent ? '0px' : '8px',
                margin: isMobile ? '15px 0px 10px' : '0px',
                transition: 'all 250ms ease-out',
                '&:hover': {
                  borderColor: isMobile ? 'transparent' : 'dianomiGreen.main',
                  borderRadius: isMobile ? '0px' : '4px',
                  backgroundColor: isMobile ? 'transparent' : 'rgba(2, 3, 3, 0.1)',
                  cursor: isMobile ? 'default' : 'pointer',
                },
              })}
              onClick={handleClick}
              ref={buttonRef}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'start',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                {/* Logo - placeholder or real */}
                {isPlaceholderLogo ? (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: '600',
                      width: sidebarOpen ? '32px' : '38px',
                      height: sidebarOpen ? '32px' : '38px',
                      borderRadius: '4px',
                      border: '1px solid #e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'primary.main',
                      backgroundColor: 'secondary.main',
                      flexShrink: 0,
                      transition: 'all 250ms ease-out',
                    }}
                  >
                    {altLogoText || '...'}
                  </Typography>
                ) : (
                  <Box
                    component="img"
                    src={displayLogo}
                    alt={sessionData?.name}
                    sx={{
                      width: sidebarOpen && !isMobile ? '32px' : '38px',
                      height: sidebarOpen && !isMobile ? '32px' : '38px',
                      borderRadius: '4px',
                      border: '1px solid #e0e0e0',
                      objectFit: 'contain',
                      flexShrink: 0,
                      transition: 'all 250ms ease-out',
                    }}
                  />
                )}
                <Box
                  sx={{
                    display: sidebarOpen ? 'flex' : 'none',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'start',
                    opacity: sidebarOpen ? 1 : 0,
                    overflow: 'hidden',
                    transition: 'opacity 150ms ease-out 150ms',
                    ml: 0,
                  }}
                >
                  {sessionData && sessionData.name ? (
                    <Typography
                      sx={{
                        fontSize: isMobile ? '10px' : '9px',
                        overflow: 'hidden',
                        maxWidth: isMobile ? '200px' : '130px',
                        color: 'primary.main',
                        padding: '0px',
                        textWrap: 'pretty',
                        textAlign: 'left',
                      }}
                    >
                      {sessionData.name}
                    </Typography>
                  ) : (
                    <Box sx={{ color: 'primary.main', whiteSpace: 'nowrap' }}>Select a client</Box>
                  )}
                  {sessionData ? (
                    <Typography
                      sx={(theme) => ({
                        color: theme.palette.dianomiGreen.main,
                        fontSize: '9px',
                        fontWeight: '600',
                        textAlign: 'left',
                        maxWidth: '110px',
                      })}
                    >
                      {label}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
              <UnfoldMore
                sx={{
                  fontSize: '16px',
                  display: sidebarOpen ? 'block' : 'none',
                  opacity: sidebarOpen ? 1 : 0,
                  transition: 'opacity 150ms ease-out 150ms',
                  ':hover': { transform: 'scale(1.2)' },
                  color: 'primary.main',
                  flexShrink: 0,
                  ml: 0.5,
                }}
              />
            </Button>
          </Tooltip>
          {!isMobile && open ? (
            <ClickAwayListener onClickAway={handleClose}>
              <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-menu"
                aria-describedby="modal-modal-menu"
                sx={{ zIndex: '2100' }}
                data-testid="navbar-modal"
                slotProps={{
                  backdrop: {
                    sx: {
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    },
                  },
                }}
              >
                <Box
                  sx={(theme) => ({
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -40%)',
                    [theme.breakpoints.up('lg')]: {
                      transform: 'translate(-50%, -50%)',
                    },
                  })}
                >
                  <Paper
                    sx={(theme) => ({
                      minWidth: isSingleTabMenu ? '40vw' : '70vw',
                      borderRadius: '5px',
                      padding: '10px',
                      [theme.breakpoints.up('lg')]: {
                        minWidth: !isSingleTabMenu && '60vw',
                      },
                    })}
                  >
                    <SectionTitle
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '10px',
                        marginBottom: '0px',
                        width: '100%',
                        fontSize: isMobile ? '18px' : '28px',
                      }}
                    >
                      Select an account:
                      <Button
                        onClick={handleClick}
                        sx={{
                          position: 'absolute',
                          right: isSingleTabMenu ? '0px' : '10px',
                          top: isSingleTabMenu ? '0px' : '10px',
                          borderRadius: '50%',
                          height: '30px',
                          width: '30px',
                          minWidth: 0,
                        }}
                      >
                        <CloseRounded
                          sx={{
                            fontSize: isSingleTabMenu ? '1em' : '2em',
                            transition: 'transform 0.2s ease-in-out',
                            transform: 'rotate(0deg)',
                            '&:hover': {
                              transform: 'rotate(90deg)',
                            },
                          }}
                        />
                      </Button>
                    </SectionTitle>
                    <ClientsSelector
                      onDoneClick={() => {
                        setOpen(false);
                        setLoginSeenCookie();
                      }}
                      accounts={accountsData?.accounts}
                    />
                  </Paper>
                </Box>
              </Modal>
            </ClickAwayListener>
          ) : (
            <ClickAwayListener onClickAway={handleClose}>
              <Popper
                id="client-selector"
                open={open && isOpen}
                transition
                anchorEl={anchorEl}
                placement="bottom-start"
                data-testid="navbar-modal"
                sx={{
                  zIndex: 2100,
                  width: '100%',
                }}
              >
                {({ TransitionProps }) => (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <Grow {...TransitionProps} id="menu-list" style={{ transformOrigin: '0 0 0' }}>
                    <Paper>
                      <ClientsSelector
                        onDoneClick={() => {
                          setOpen(false);
                          setLoginSeenCookie();
                          if (isMobile && onClientChange) onClientChange();
                        }}
                        accounts={accountsData?.accounts}
                      />
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </ClickAwayListener>
          )}
        </>
      )}
    </div>
  );
}

export default NavbarClientSelector;
